import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import ThemeToggle from "@/components/ui/ThemeToggle";
import Logo from "@/components/common/Logo";
import Container from "@/components/ui/Container";
import SidePanel from "@/components/extension/SidePanel";

// The desktop browser mock and the mobile frame used to BOTH render a <SidePanel>,
// hidden from each other only by `lg:block` / `lg:hidden`. Both were mounted at
// every viewport — two copies of the panel's state, timers and API traffic on the
// public marketing page. Pick the frame in JS so only one ever exists.
function useIsDesktop(query = "(min-width: 1024px)") {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

// The website "under test" — purely a backdrop so the docked panel reads as a
// real side panel sitting next to a live page.
function FakePage() {
  return (
    <div className="h-full overflow-hidden bg-gradient-to-b from-sky-600 to-blue-800 text-white">
      <div className="flex items-center justify-between px-6 py-4 text-sm">
        <span className="flex items-center gap-2 font-display text-lg font-bold">
          <span className="grid h-6 w-6 place-items-center rounded-md bg-white/20 text-xs">◆</span>
          Acme
        </span>
        <nav className="hidden gap-5 text-sky-50/90 sm:flex">
          <span>Features</span>
          <span>How it works</span>
          <span>Pricing</span>
        </nav>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-blue-700">
          Get started
        </span>
      </div>
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <h1 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl">
          SHIP FASTER.
          <br /> BREAK LESS.
        </h1>
        <p className="mt-4 max-w-md text-sm text-sky-50/90">
          The all-in-one workspace where your team plans, builds, and ships
          together.
        </p>
        <button className="mt-7 rounded-full bg-white px-6 py-3 text-sm font-bold text-blue-700">
          TRY IT FREE ↗
        </button>
      </div>
    </div>
  );
}

export default function Extension() {
  const isDesktop = useIsDesktop();
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-ink-950">
      {/* slim top bar */}
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80">
        <Container className="flex h-16 items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 dark:text-slate-300"
            >
              <ArrowLeft size={16} /> Back
            </Link>
            <ThemeToggle />
          </div>
        </Container>
      </header>

      <Container className="py-10 sm:py-14">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-700 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-300">
            Chrome side-panel · live preview
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Test the page you&apos;re on
          </h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            Sign in, then type a test or tap a quick prompt. Watch the plan run,
            get the bug, see the cost — without leaving the page. Try it below.
          </p>
        </div>

        {/* desktop: browser window with docked panel. mobile: phone frame.
            Exactly one of these mounts — see useIsDesktop above. */}
        <div className="mt-10">
          {isDesktop ? (
            <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-ink-900">
              {/* window chrome */}
              <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100/70 px-4 py-2.5 dark:border-white/10 dark:bg-white/5">
                <div className="flex gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-rose-400" />
                  <span className="h-3 w-3 rounded-full bg-amber-400" />
                  <span className="h-3 w-3 rounded-full bg-emerald-400" />
                </div>
                <div className="flex flex-1 items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs text-slate-500 dark:bg-ink-950 dark:text-slate-400">
                  <Lock size={12} /> app.acme.com/login
                </div>
              </div>
              {/* page + docked panel */}
              <div className="flex h-[640px]">
                <div className="flex-1">
                  <FakePage />
                </div>
                <div className="w-[400px] shrink-0 border-l border-slate-200 dark:border-white/10">
                  <SidePanel />
                </div>
              </div>
            </div>
          ) : (
            /* mobile / tablet: standalone panel frame */
            <div className="mx-auto w-full max-w-sm overflow-hidden rounded-3xl border border-slate-200 shadow-soft dark:border-white/10">
              <div className="h-[640px]">
                <SidePanel />
              </div>
            </div>
          )}
        </div>

        <p className="mx-auto mt-8 max-w-xl text-center text-xs text-slate-400">
          This is the in-app preview of the side panel. It ships with a{" "}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-white/10">
            manifest.json
          </code>{" "}
          so it converts into a real Chrome MV3 extension that reads the active
          tab&apos;s URL.
        </p>
      </Container>
    </div>
  );
}
